import {
  Button,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Tab,
  Tabs,
  TabsHeader,
  Typography
} from "@material-tailwind/react";
import React, {useContext, useEffect, useRef, useState} from "react";
import {motion} from "framer-motion";
import "../../style/datePicker.css"
import {ServiceDocumentGrid} from "./agent_service_document-grid";
import {ServiceDetailForm} from "./service_detail_form";
import {ServiceContext} from "../../context/service";
import {getServicePaymentAndSteps} from "../../services/service/service_step";
import persianNumberFormat from "../../helper/PersianNumberFormat";
import {PersonServiceSideCostGrid} from "./person_service_side_cost_grid";
import {PersonServiceStepsGrid} from "./person_service_step_grid";
import {ServiceCheckDialog} from "./service_check_dialog";
import {
  BsBell,
  BsBriefcase,
  BsCheckCircle,
  BsCreditCard2Front,
  BsFileEarmarkText,
  BsPerson,
} from "react-icons/bs";

const profileServiceModal = process.env.NODE_ENV !== "production";

const STATUS_BADGE = {
  new:            { label: "در انتظار تکمیل مدارک",       className: "border-blue-200 bg-blue-50 text-blue-700" },
  waiting:        { label: "در انتظار پرداخت",             className: "border-amber-200 bg-amber-50 text-amber-700" },
  second_waiting: { label: "در انتظار پرداخت مرحله دوم",  className: "border-amber-200 bg-amber-50 text-amber-700" },
  payment:        { label: "پرداخت شده",                   className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  finish:         { label: "اتمام خدمت",                   className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  cancel:         { label: "لغو شده",                      className: "border-rose-200 bg-rose-50 text-rose-700" },
};

const DocumentUploadBanner = ({count, onClick}) => (
  <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-gray-100 bg-blue-gray-50 px-4 py-3">
    <div className="flex items-center gap-3">
      <BsBell className="h-5 w-5 shrink-0 text-blue-gray-600" />
      <div>
        <p className="text-sm font-bold text-blue-gray-800">
          {persianNumberFormat(count)} مدرک نیاز به بارگذاری دارد
        </p>
        <p className="mt-0.5 text-xs text-blue-gray-500">
          برای ادامه فرایند، مدارک مشخص‌شده را تکمیل کنید.
        </p>
      </div>
    </div>
    <button
      type="button"
      onClick={onClick}
      className="rounded-md bg-blue-gray-800 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-blue-gray-900"
    >
      مشاهده مدارک
    </button>
  </div>
);

export function PersonServiceCheckForm() {
  const {
    service,
    setService,
    modalOpen,
    setModalOpen,
    lastDataRefresh,
    setLastDataRefresh,
    setStep,
    serviceSteps,
    setServiceSteps,
    setBranchInfo,
    totalDays,
    setTotalDays,
    totalSideCost,
    setTotalSideCosts,
    sideCosts,
    setSideCosts,
    show,
    setShow,
    serviceDetailCacheRef
  } = useContext(ServiceContext);

  const closeModal = () => {
    setModalOpen(false);
  };

  const [allAccepted, setAllAccepted] = useState(false);

  const [serviceDocumentations, setServiceDocumentations] = useState([])
  const [loadedServiceId, setLoadedServiceId] = useState(null)
  const modalOpenedAtRef = useRef(null)

  const needsUserDocumentation = (documents) => Array.isArray(documents)
    && documents.some(document => document.status === "new" || document.status === "rejected");

  const applyServiceDetails = (data, serviceId) => {
    const startedAt = profileServiceModal ? performance.now() : 0;
    const {
      serviceDocumentationList,
      service_agent_branch_fee_info,
      service_steps,
      service_side_costs,
    } = data;

    const documentations = serviceDocumentationList || [];
    setServiceDocumentations(documentations);

    if (needsUserDocumentation(documentations)) {
      setShow(1);
    }

    const documentationsAccepted = documentations.length > 0
      ? documentations.every(obj => obj.status === "accepted")
      : true;

    const stepsAccepted = service_steps?.length > 0
      ? service_steps.every(obj => obj.days > 0)
      : false;

    setAllAccepted(documentationsAccepted && stepsAccepted);
    setBranchInfo(service_agent_branch_fee_info)
    setServiceSteps(service_steps)

    const total_days = Array.isArray(service_steps)
      ? service_steps.reduce((sum, step) => sum + (step.days || 0), 0)
      : 0;

    setService(service => ({
      ...service,
      days: total_days,
    }))
    setTotalDays(total_days)

    setSideCosts(service_side_costs || [])
    const total_side_costs = Array.isArray(service_side_costs)
      ? service_side_costs.reduce((sum, sideCost) => sum + (sideCost.cost || 0), 0)
      : 0;

    setTotalSideCosts(total_side_costs)
    setLoadedServiceId(serviceId)

    if (profileServiceModal) {
      console.debug("[service-modal][person] apply details", {
        serviceId,
        durationMs: Math.round(performance.now() - startedAt),
        documents: documentations.length,
        steps: Array.isArray(service_steps) ? service_steps.length : 0,
        sideCosts: Array.isArray(service_side_costs) ? service_side_costs.length : 0,
      });
    }
  };

  useEffect(() => {
    if (!profileServiceModal || !modalOpen || !service?.id) {
      return;
    }

    modalOpenedAtRef.current = performance.now();
    console.debug("[service-modal][person] open", {
      serviceId: service.id,
      cached: Boolean(serviceDetailCacheRef?.current?.[service.id]),
      step: show,
    });
  }, [modalOpen, service?.id]);

  useEffect(() => {
    if (!profileServiceModal || loadedServiceId !== service?.id || !modalOpenedAtRef.current) {
      return;
    }

    console.debug("[service-modal][person] ready", {
      serviceId: service.id,
      durationMs: Math.round(performance.now() - modalOpenedAtRef.current),
    });
  }, [loadedServiceId, service?.id]);

  useEffect(() => {
    let ignore = false;

    async function getData() {
      const startedAt = profileServiceModal ? performance.now() : 0;
      const result = await getServicePaymentAndSteps(service.id);
      if (profileServiceModal) {
        console.debug("[service-modal][person] getServicePaymentAndSteps", {
          serviceId: service.id,
          status: result.status,
          durationMs: Math.round(performance.now() - startedAt),
        });
      }
      if (!ignore && result.status === 200) {
        if (serviceDetailCacheRef?.current) {
          serviceDetailCacheRef.current[service.id] = result.data;
        }
        applyServiceDetails(result.data, service.id)
      }
    }

    if (service?.id) {
      const cachedDetails = serviceDetailCacheRef?.current?.[service.id];
      if (cachedDetails) {
        if (profileServiceModal) {
          console.debug("[service-modal][person] cache hit", {serviceId: service.id});
        }
        applyServiceDetails(cachedDetails, service.id)
      }

      if (!cachedDetails || lastDataRefresh) {
        if (profileServiceModal) {
          console.debug("[service-modal][person] cache miss/fetch", {
            serviceId: service.id,
            refresh: Boolean(lastDataRefresh),
          });
        }
        getData()
      }
    }

    return () => {
      ignore = true;
    }
  }, [service?.id, lastDataRefresh]);

  if (service) {
    const detailsLoaded = loadedServiceId === service.id;
    const pendingDocumentCount = serviceDocumentations.filter(
      document => document.status === "new" || document.status === "rejected"
    ).length;
    const tabs = [
      serviceDocumentations.length > 0 && {
        value: 1,
        label: "مدارک مورد نیاز",
        needsAttention: pendingDocumentCount > 0,
        content: (
          <ServiceDocumentGrid
            serviceDocuments={serviceDocumentations}
            attentionStatuses={["new", "rejected"]}
            actionLabel="بارگذاری مدرک"
          />
        ),
      },
      {
        value: 2,
        label: "مراحل انجام خدمت",
        content: (
          <div className="flex flex-col justify-center max-w-full overflow-x-auto">
            {serviceSteps?.length > 0 &&
              <PersonServiceStepsGrid/>
            }
          </div>
        ),
      },
      sideCosts?.length > 0 && {
        value: 3,
        label: "هزینه های دولتی و جانبی",
        content: (
          <div className="flex flex-col justify-center max-w-full overflow-x-auto">
            <PersonServiceSideCostGrid/>
          </div>
        ),
      },
      {
        value: 4,
        label: "قیمت و اطلاعات خدمات",
        content: <ServiceDetailForm/>,
      },
    ].filter(Boolean);
    const activeTab = tabs.some(tab => tab.value === show) ? show : tabs[0]?.value;
    const activeTabContent = tabs.find(tab => tab.value === activeTab)?.content;
    const statusBadge = STATUS_BADGE[service.status] ?? {
      label: service.status || "—",
      className: "border-slate-200 bg-slate-50 text-slate-600",
    };
    const acceptedDocumentCount = serviceDocumentations.filter(
      document => document.status === "accepted"
    ).length;
    const canPay = detailsLoaded && (
      service.status === "waiting" ||
      service.status === "second_waiting" ||
      (service.price && allAccepted && service.status !== "payment")
    );
    const agentName = `${service.agent_name || ""} ${service.agent_family || ""}`.trim();

    return (
      <>
        <ServiceCheckDialog
          open={modalOpen}
          onClose={closeModal}
          className="max-h-screen overflow-y-scroll"
        >
          <DialogHeader className="border-b border-blue-gray-100 bg-blue-gray-50 px-5 py-4">
            <div className="flex w-full flex-col gap-3">

              {/* title + status + id */}
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Typography variant="h6" color="blue-gray" className="font-bold">
                      درخواست خدمات
                    </Typography>
                    <span className={`rounded-full border px-2.5 py-0.5 text-xs font-bold ${statusBadge.className}`}>
                      {statusBadge.label}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-sm text-blue-gray-600">
                    {service.field_title}{service.branch_title ? ` — ${service.branch_title}` : ""}
                  </p>
                </div>
                <span className="rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-blue-gray-500 shadow-sm">
                  #{service.id}
                </span>
              </div>

              {/* meta row */}
              <div className="flex flex-wrap gap-x-5 gap-y-1.5 border-t border-blue-gray-100 pt-3 text-xs text-blue-gray-600">
                {agentName && (
                  <span className="flex items-center gap-1.5">
                    <BsPerson className="h-3.5 w-3.5 text-blue-gray-400" />
                    <span className="text-blue-gray-400">مباشر:</span>
                    <span className="font-semibold text-blue-gray-700">{agentName}</span>
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <BsBriefcase className="h-3.5 w-3.5 text-blue-gray-400" />
                  <span className="text-blue-gray-400">خدمت:</span>
                  <span className="font-semibold text-blue-gray-700">{service.branch_title || "—"}</span>
                </span>
                {serviceDocumentations.length > 0 && (
                  <span className="flex items-center gap-1.5">
                    <BsFileEarmarkText className="h-3.5 w-3.5 text-blue-gray-400" />
                    <span className="text-blue-gray-400">مدارک:</span>
                    <span className="font-semibold text-blue-gray-700">
                      {acceptedDocumentCount} از {serviceDocumentations.length} تایید شده
                    </span>
                  </span>
                )}
              </div>
            </div>
          </DialogHeader>

          <DialogBody className="flex flex-col gap-4 overflow-y-scroll bg-white px-4 py-4 md:px-5">
            {!detailsLoaded && (
              <div className="flex min-h-[16rem] flex-col items-center justify-center gap-3 text-center">
                <span className="h-10 w-10 animate-pulse rounded-full bg-blue-gray-100" />
                <Typography color="blue-gray" className="font-medium">
                  در حال دریافت اطلاعات...
                </Typography>
              </div>
            )}

            {detailsLoaded && (
              <>
              {pendingDocumentCount > 0 && (
                <DocumentUploadBanner
                  count={pendingDocumentCount}
                  onClick={() => setShow(1)}
                />
              )}
              <div className="rounded-xl border border-blue-gray-100 bg-white shadow-sm">
                <div className="flex flex-col p-3 md:p-5">
                  <Tabs value={activeTab} className="w-full">
                    <TabsHeader
                      className="w-full overflow-x-auto rounded-md bg-blue-gray-50 p-1"
                      indicatorProps={{className: "bg-white shadow-sm rounded-md"}}
                    >
                      {tabs.map(({value, label, needsAttention}) => (
                        <Tab
                          key={value}
                          value={value}
                          onClick={() => setShow(value)}
                          className="min-w-fit px-4 py-2 text-sm md:text-base"
                        >
                          <span className="flex items-center justify-center gap-2 whitespace-nowrap">
                            {label}
                            {needsAttention && (
                              <span className="relative inline-flex">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                                <span className="relative flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">
                                  {pendingDocumentCount}
                                </span>
                              </span>
                            )}
                          </span>
                        </Tab>
                      ))}
                    </TabsHeader>
                  </Tabs>
                  <motion.div
                    key={activeTab}
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    transition={{duration: 0.05}}
                    className="mt-4 rounded-md border border-blue-gray-100 bg-white p-3 md:p-4"
                  >
                    {activeTabContent}
                  </motion.div>
                </div>
              </div>
              </>
            )}
          </DialogBody>

          <DialogFooter className="flex flex-wrap items-center justify-between gap-3 border-t border-blue-gray-100 bg-blue-gray-50 px-5 py-4">
            <div className="flex items-center gap-2 text-xs text-blue-gray-500">
              {allAccepted ? (
                <>
                  <BsCheckCircle className="h-4 w-4 text-green-600" />
                  مدارک و مراحل آماده ادامه فرایند هستند
                </>
              ) : (
                <>
                  <BsFileEarmarkText className="h-4 w-4 text-blue-gray-400" />
                  وضعیت خدمت پس از تکمیل اطلاعات به‌روزرسانی می‌شود
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button variant="text" color="gray" onClick={closeModal}>
                <span>بستن</span>
              </Button>
              {canPay && (
                <Button
                  variant="gradient"
                  color="gray"
                  onClick={() => setStep("invoice")}
                  className="flex items-center gap-2"
                >
                  <BsCreditCard2Front className="h-4 w-4" />
                  <span>پرداخت</span>
                </Button>
              )}
            </div>
          </DialogFooter>
        </ServiceCheckDialog>
      </>
    );
  }
}
